import type { SiteLang } from "@/hooks/useLanguage";

/* ───────────────────────────────────────────
   UI chrome strings used across service detail
   components (ServiceHero, ServiceWhyQbd, etc.)
   ─────────────────────────────────────────── */

const serviceDetailUI = {
  en: {
    // Breadcrumbs
    home: "Home",
    services: "Services",
    // ServiceHero
    discoverSolutions: "Discover our offerings",
    getInTouch: "Get in touch",
    // ServiceValueProps — no extra chrome
    // ServiceChallenges
    industryContext: "Industry context",
    // ServiceOfferings
    offerings: "Offerings",
    whatWeOffer: "What we offer",
    all: "All",
    pharma: "Pharma",
    ivd: "IVD",
    // ServiceIndustryLinks
    ourServices: "Our services",
    clinicalCROServices: "Our Clinical CRO Services",
    raByIndustry: "Regulatory Affairs by Industry",
    learnMore: "Learn more",
    // ServiceWhyQbd
    whyQbdGroup: "Why QbD Group",
    yourTrustedPartner: "Your trusted partner",
    partnerWithUs: "Partner with us",
    // ServiceRelatedContent
    latestInsights: "Latest insights",
    relatedContent: "Related content",
    viewAll: "View all",
    caseStudy: "Case Study",
    whitePaper: "White Paper",
    webinar: "Webinar",
    regulatoryUpdate: "Regulatory Update",
    news: "News",
    podcast: "Podcast",
    blog: "Blog",
    // ContactCTA — already i18n via homepage
  },
  es: {
    home: "Inicio",
    services: "Servicios",
    discoverSolutions: "Descubre nuestras ofertas",
    getInTouch: "Contactar",
    industryContext: "Contexto de la industria",
    offerings: "Nuestra oferta",
    whatWeOffer: "Qué ofrecemos",
    all: "Todos",
    pharma: "Farma",
    ivd: "IVD",
    ourServices: "Nuestros servicios",
    clinicalCROServices: "Nuestros servicios CRO en clínica",
    raByIndustry: "Regulatory Affairs por industria",
    learnMore: "Saber más",
    whyQbdGroup: "Por qué QbD Group",
    yourTrustedPartner: "Tu socio de confianza",
    partnerWithUs: "Colabora con nosotros",
    latestInsights: "Últimos insights",
    relatedContent: "Contenido relacionado",
    viewAll: "Ver todo",
    caseStudy: "Caso de estudio",
    whitePaper: "White Paper",
    webinar: "Webinar",
    regulatoryUpdate: "Actualización regulatoria",
    news: "Noticias",
    podcast: "Podcast",
    blog: "Blog",
  },
  "zh-cn": {
    home: "首页",
    services: "服务",
    discoverSolutions: "探索我们的解决方案",
    getInTouch: "联系我们",
    industryContext: "行业背景",
    offerings: "服务项目",
    whatWeOffer: "我们提供",
    all: "全部",
    pharma: "制药",
    ivd: "IVD",
    ourServices: "我们的服务",
    clinicalCROServices: "我们的临床CRO服务",
    raByIndustry: "按行业划分的法规事务",
    learnMore: "了解更多",
    whyQbdGroup: "为什么选择QbD Group",
    yourTrustedPartner: "您值得信赖的合作伙伴",
    partnerWithUs: "与我们合作",
    latestInsights: "最新洞察",
    relatedContent: "相关内容",
    viewAll: "查看全部",
    caseStudy: "案例研究",
    whitePaper: "白皮书",
    webinar: "网络研讨会",
    regulatoryUpdate: "法规更新",
    news: "新闻",
    podcast: "播客",
    blog: "博客",
  },
};

export type ServiceDetailT = (typeof serviceDetailUI)["en"];

export const getServiceDetailT = (lang: SiteLang): ServiceDetailT =>
  serviceDetailUI[lang] ?? serviceDetailUI.en;

/* ───────────────────────────────────────────
   Per-service content translations.
   Key = service slug. Only services with
   Spanish content are listed here.
   ─────────────────────────────────────────── */

export interface ServiceContentI18n {
  title: string;
  subtitle: string;
  description: string;
  valueProps: { title: string; description: string }[];
  challengesTitle: string;
  challengesContent: string;
  whyQbd: {
    intro: string;
    bullets: string[];
  };
  formHeading: string;
  formSubtitle: string;
  industryLinks?: { title: string; description: string }[];
}

const serviceContent: Record<string, Partial<Record<SiteLang, ServiceContentI18n>>> = {
  "clinical-solutions": {
    es: {
      title: "Clinical Solutions para Medical Devices, IVD y CDx",
      subtitle: "Soporte clínico integral para innovadores en MedTech",
      description:
        "Soporte clínico integral que ayuda a los innovadores en MedTech a avanzar desde la estrategia temprana hasta la ejecución lista para la aprobación regulatoria, con confianza, rapidez y cumplimiento normativo. Apoyamos a fabricantes de Medical Devices, IVD y CDx con estrategia clínica integrada, diseño de estudios y ejecución de ensayos.",
      valueProps: [
        {
          title: "Ejecución clínica de principio a fin",
          description:
            "Desde el análisis de brechas hasta la ejecución completa como CRO, apoyamos a fabricantes de Medical Devices, IVD y CDx con estrategia clínica integrada, diseño de estudios y ejecución de ensayos.",
        },
        {
          title: "Confianza regulatoria desde el diseño",
          description:
            "Nuestros expertos aseguran que la evidencia clínica cumpla con los requisitos del MDR, IVDR y evaluación del rendimiento, sin sorpresas en las fases finales.",
        },
        {
          title: "Experiencia especializada para innovadores MedTech",
          description:
            "Como CRO de servicio integral, te guiamos desde la planificación temprana de la evidencia hasta las operaciones clínicas y las actividades post-comercialización.",
        },
      ],
      challengesTitle: "Excelencia clínica en Medical Devices, IVD y CDx",
      challengesContent:
        "En QbD Group, proporcionamos desarrollo clínico y validación para Medical Devices, IVD y CDx, combinando análisis de brechas de alta calidad, redacción médica experta y ejecución completa como CRO.\n\nTanto si preparas una primera investigación clínica, generas evidencia post-comercialización o navegas las complejidades del codesarrollo de diagnósticos companion, nuestro equipo multidisciplinar garantiza que tu programa clínico esté diseñado para el éxito regulatorio desde el primer día.",
      whyQbd: {
        intro:
          "QbD Group combina una profunda experiencia en MedTech con un enfoque centrado en el cliente. Nuestro equipo clínico te guía desde la planificación temprana de la evidencia hasta la ejecución lista para la aprobación regulatoria.",
        bullets: [
          "Experiencia en MedTech — dedicados exclusivamente a Medical Devices, IVD y diagnósticos companion.",
          "Socio de confianza — trayectoria demostrada con innovadores y empresas MedTech consolidadas.",
          "Calidad en cada paso — procesos rigurosos que aseguran evidencia clínica acorde a las expectativas regulatorias.",
          "Soluciones a medida y ajustadas al presupuesto — escalamos nuestros servicios según las necesidades y plazos de tu proyecto.",
        ],
      },
      formHeading: "Planifica tu estrategia clínica",
      formSubtitle:
        "Desde el análisis de brechas hasta la ejecución completa como CRO, nuestros expertos clínicos están listos para acompañarte en tu camino MedTech.",
      industryLinks: [
        {
          title: "CRO para Medical Devices",
          description:
            "Estudios clínicos y generación de evidencia para Medical Devices a lo largo de todo su ciclo de vida. Desde estudios de viabilidad temprana y estudios pivotales globales hasta el seguimiento clínico post-comercialización, apoyamos a fabricantes de Medical Devices con estrategia clínica, ejecución de estudios y evidencia alineada con el MDR.",
        },
        {
          title: "CRO para In Vitro Diagnostics",
          description:
            "Estudios de rendimiento clínico que respaldan el cumplimiento del IVDR. Ayudamos a los desarrolladores de IVD a diseñar y ejecutar estudios de rendimiento, garantizando que la evidencia clínica cumpla los requisitos del IVDR y las expectativas de los organismos notificados.",
        },
        {
          title: "Companion Diagnostics (CDx)",
          description:
            "Soporte clínico y regulatorio integrado para el desarrollo de CDx. Acompañamos programas de CDx en estrecha alineación con el desarrollo del fármaco, asegurando que la evidencia clínica, la estrategia regulatoria y los plazos se mantengan sincronizados.",
        },
      ],
    },
    "zh-cn": {
      title: "面向医疗器械、IVD 与 CDx 的临床解决方案",
      subtitle: "为 MedTech 创新者提供端到端的临床支持",
      description:
        "端到端的临床支持,帮助 MedTech 创新者从早期战略推进到法规就绪的执行,做到自信、迅速且合规。我们以整合的临床战略、研究设计和试验执行,支持医疗器械、IVD 与 CDx 制造商。",
      valueProps: [
        {
          title: "端到端的临床执行",
          description:
            "从差距分析到完整 CRO 执行,我们以整合的临床战略、研究设计和试验执行,支持医疗器械、IVD 与 CDx 制造商。",
        },
        {
          title: "从设计起就具备的法规可信度",
          description:
            "我们的专家确保临床证据满足 MDR、IVDR 与性能评估的要求,后期不会出现意外。",
        },
        {
          title: "面向 MedTech 创新者的专业经验",
          description:
            "作为全方位 CRO,我们引导您从早期证据规划到临床运营和上市后活动。",
        },
      ],
      challengesTitle: "面向医疗器械、IVD 与 CDx 的卓越临床",
      challengesContent:
        "在 QbD Group,我们为医疗器械、IVD 与 CDx 提供临床开发与验证,结合高质量的差距分析、专业的医学撰写和完整的 CRO 执行能力。\n\n无论您是在准备首次临床研究、生成上市后证据,还是在应对伴随诊断协同开发的复杂性,我们的多学科团队都能确保您的临床项目从第一天起就为法规成功而设计。",
      whyQbd: {
        intro:
          "QbD Group 将深厚的 MedTech 专长与以客户为中心的方法相结合。我们的临床团队引导您从早期证据规划到法规就绪的执行。",
        bullets: [
          "MedTech 专长 — 专注服务于医疗器械、IVD 和伴随诊断。",
          "可信赖的合作伙伴 — 在与新兴及成熟 MedTech 公司的合作中拥有可证实的业绩。",
          "每一步都注重质量 — 严谨的流程确保临床证据符合法规期望。",
          "量身定制且预算合理的解决方案 — 我们根据您的项目需求和时间线灵活调整服务规模。",
        ],
      },
      formHeading: "规划您的临床路径",
      formSubtitle:
        "从差距评估到完整 CRO 交付,我们的临床专家随时为您的 MedTech 旅程提供支持。",
      industryLinks: [
        {
          title: "医疗器械 CRO",
          description:
            "覆盖医疗器械全生命周期的临床研究与证据生成。从早期可行性研究和全球关键性研究,到上市后临床随访,我们以临床战略、研究执行和符合 MDR 的证据生成,支持医疗器械制造商。",
        },
        {
          title: "体外诊断 CRO",
          description:
            "支持 IVDR 合规的临床性能研究。我们帮助 IVD 开发者设计并执行性能研究,确保临床证据满足 IVDR 要求和公告机构的期望。",
        },
        {
          title: "伴随诊断 (CDx)",
          description:
            "为 CDx 开发提供整合的临床与法规支持。我们与药物开发紧密协同地支持 CDx 项目,确保临床证据、法规策略和时间线保持同步。",
        },
      ],
    },
  },
  "qualification-validation": {
    es: {
      title: "Qualification & Validation",
      subtitle: "Garantizando productos consistentes y de alta calidad",
      description:
        "Desde el commissioning de instalaciones hasta la cualificación de equipos, proporcionamos soporte escalable y flexible para asegurar que tus productos cumplan los más altos estándares de calidad y regulatorios.",
      valueProps: [
        {
          title: "Experiencia demostrada en proyectos de gran escala",
          description:
            "Aportamos una amplia experiencia en la gestión de proyectos complejos, incluyendo la cualificación de utilities e instalaciones, la cualificación de equipos de laboratorio y de proceso, y la validación de métodos analíticos.",
        },
        {
          title: "Soluciones a medida con la combinación perfecta de expertos",
          description:
            "A través de nuestra avanzada matriz de competencias, garantizamos una combinación perfecta entre tus requisitos específicos y nuestros expertos.",
        },
        {
          title: "Enfoque escalable, flexible y transparente",
          description:
            "Nos adaptamos de forma fluida a las demandas del proyecto, ajustamos los recursos según sea necesario y ofrecemos actualizaciones claras y en tiempo real sobre el progreso y el presupuesto.",
        },
        {
          title: "Conocimiento integral a lo largo del ciclo de vida del proyecto",
          description:
            "Nuestro profundo conocimiento abarca todo el espectro de la cualificación y validación, desde salas limpias y maquinaria hasta productos y procesos en proyectos de Life Sciences.",
        },
      ],
      challengesTitle: "Tendencias del mercado y desafíos",
      challengesContent:
        "Las regulaciones en evolución y las nuevas tecnologías están transformando la cualificación y validación en Life Sciences.\n\n**Requisitos más estrictos**: los organismos reguladores elevan el listón, exigiendo una documentación más exhaustiva, pruebas más rigurosas y un seguimiento continuo.\n\n**Sostenibilidad**: las empresas están bajo presión para reducir el impacto ambiental mediante procesos de validación más eficientes.\n\n**Escalado de operaciones**: el crecimiento demanda soluciones de C&V escalables que mantengan la calidad y el cumplimiento en carteras más amplias.",
      whyQbd: {
        intro:
          "Cuando se trata de servicios de cualificación y validación, QbD Group aporta más de una década de experiencia y un equipo de más de 700 especialistas a cada proyecto. Nos comprometemos a ofrecer soluciones integrales de principio a fin que garanticen la ejecución impecable de tus proyectos.",
        bullets: [
          "Soporte integral a lo largo del ciclo de vida: desde el commissioning hasta la validación continua de procesos, cubrimos cada fase de tu proyecto.",
          "Alcance global, experiencia local: nuestros equipos están integrados en toda Europa, dándote acceso a expertos que entienden el panorama regulatorio local.",
          "Experiencia demostrada: con una trayectoria que abarca cientos de proyectos de cualificación y validación a gran escala, aportamos una experiencia inigualable en la industria.",
          "Soluciones escalables: flexibilizamos nuestros recursos para adaptarnos a las necesidades de tu proyecto, proporcionando la experiencia adecuada en el momento adecuado.",
        ],
      },
      formHeading: "Hablemos de cualificación y validación",
      formSubtitle:
        "Ya sea que te enfrentes a un proyecto de validación a gran escala o necesites soporte continuo, nuestros expertos están listos para guiarte en cada etapa.",
    },
    "zh-cn": {
      title: "确认与验证服务",
      subtitle: "保障产品的一致性与高质量",
      description:
        "从设施调试到设备确认,我们提供可扩展、灵活的支持,确保您的产品达到最高的质量与法规标准。",
      valueProps: [
        {
          title: "大型项目的成熟经验",
          description:
            "我们在管理复杂项目方面拥有丰富经验,涵盖公用工程与设施确认、实验室及工艺设备确认,以及分析方法验证。",
        },
        {
          title: "由合适专家组合打造的定制方案",
          description:
            "通过我们先进的能力矩阵,确保您的具体需求与我们专家之间的完美匹配。",
        },
        {
          title: "可扩展、灵活且透明的方法",
          description:
            "我们能够顺畅适应项目需求,按需调配资源,并就进度与预算提供清晰、实时的更新。",
        },
        {
          title: "贯穿项目生命周期的全面知识",
          description:
            "我们的深厚专长覆盖确认与验证的全谱系,从洁净室与机械,到 Life Sciences 项目中的产品与工艺。",
        },
      ],
      challengesTitle: "市场趋势与挑战",
      challengesContent:
        "不断演变的法规与新兴技术正在重塑 Life Sciences 领域的确认与验证。\n\n**更严格的要求**:监管机构不断提高门槛,要求更全面的文件、更严谨的测试以及持续监控。\n\n**可持续发展**:企业承受着通过更高效的验证流程来降低环境影响的压力。\n\n**运营规模化**:业务增长需要可扩展的 C&V 解决方案,以在更广泛的产品组合中保持质量与合规。",
      whyQbd: {
        intro:
          "在确认与验证服务方面,QbD Group 为每个项目带来超过十年的经验和一支 700 多名专家组成的团队。我们致力于提供端到端的整体解决方案,确保您的项目得以无瑕执行。",
        bullets: [
          "覆盖全生命周期的支持:从调试到持续工艺验证,我们覆盖项目的每一个阶段。",
          "全球布局,本地专长:我们的团队遍布欧洲,让您可以接触到了解当地法规环境的专家。",
          "成熟的经验:在数百个大型确认与验证项目中积累的业绩,为行业带来无可比拟的经验。",
          "可扩展的解决方案:我们灵活调配资源以匹配您的项目需求,在合适的时机提供合适的专长。",
        ],
      },
      formHeading: "聊聊确认与验证",
      formSubtitle:
        "无论您面对的是大型验证项目,还是需要持续的支持,我们的专家都已准备好在每个阶段为您提供指导。",
    },
  },
  "quality-assurance": {
    es: {
      title: "Quality Assurance",
      subtitle: "Tu socio de confianza en calidad y cumplimiento normativo",
      description:
        "Desde la implementación de sistemas de gestión de calidad hasta la externalización de QA y los servicios de QP/RP, ofrecemos soluciones flexibles y escalables para garantizar el cumplimiento y la excelencia operativa.",
      valueProps: [
        {
          title: "Experiencia integral en calidad",
          description:
            "Nuestros expertos cubren todo el espectro de Quality Assurance, desde la implementación de QMS hasta auditorías, remediación y servicios de Persona Cualificada.",
        },
        {
          title: "Soluciones flexibles y escalables",
          description:
            "Adaptamos nuestros servicios a tus necesidades, ofreciendo soporte desde consultores individuales hasta equipos completos de QA externalizado.",
        },
        {
          title: "Cumplimiento normativo garantizado",
          description:
            "Amplio conocimiento de GMP, GDP, GCP e ISO para asegurar que tus sistemas y procesos cumplen las expectativas regulatorias más exigentes.",
        },
        {
          title: "Flexibles y conectados en toda Europa",
          description:
            "Estés donde estés, nuestros expertos garantizan una colaboración fluida. Desde el soporte local hasta el cumplimiento a nivel europeo, estamos justo donde nos necesitas.",
        },
      ],
      challengesTitle: "Retos del Quality Assurance",
      challengesContent:
        "Las organizaciones de Life Sciences se enfrentan a requisitos regulatorios en constante evolución, la necesidad de mantener sistemas de calidad robustos y la presión de optimizar recursos sin comprometer el cumplimiento.\n\nYa se trate de implementar un nuevo QMS, remediar hallazgos de inspección, externalizar actividades de calidad o asegurar la disponibilidad de una Persona Cualificada, contar con el socio adecuado marca la diferencia.",
      whyQbd: {
        intro:
          "QbD Group aporta décadas de experiencia en Quality Assurance para organizaciones de todos los tamaños. Nuestro equipo de más de 150 expertos en QA te apoya en cada fase, desde la estrategia hasta la ejecución.",
        bullets: [
          "Más de 150 expertos en QA con experiencia en organizaciones pequeñas y grandes.",
          "Amplio conocimiento regulatorio con numerosas interacciones con autoridades competentes.",
          "Soluciones a medida adaptadas a los retos únicos de tu organización.",
          "Soporte global con capacidad presencial y remota según tus necesidades.",
        ],
      },
      formHeading: "Hablemos de Quality Assurance",
      formSubtitle:
        "Ya necesites implementar un QMS, externalizar tu QA o cubrir roles de QP/RP, nuestros expertos están listos para ayudarte.",
    },
    "zh-cn": {
      title: "质量保证",
      subtitle: "您在质量与法规合规方面值得信赖的合作伙伴",
      description:
        "从质量管理体系实施到 QA 外包以及 QP/RP 服务,我们提供灵活、可扩展的解决方案,以确保合规与卓越运营。",
      valueProps: [
        {
          title: "全面的质量专长",
          description:
            "我们的专家覆盖质量保证的全谱系,从 QMS 实施到审计、整改以及合格人员服务。",
        },
        {
          title: "灵活、可扩展的解决方案",
          description:
            "我们根据您的需求量身定制服务,从单一顾问到完整的 QA 外包团队均可提供支持。",
        },
        {
          title: "确保法规合规",
          description:
            "对 GMP、GDP、GCP 和 ISO 拥有广泛的知识储备,确保您的体系与流程满足最严苛的法规期望。",
        },
      ],
      challengesTitle: "质量保证的挑战",
      challengesContent:
        "Life Sciences 组织面临不断演变的法规要求、保持稳健质量体系的需求,以及在不影响合规性的情况下优化资源的压力。\n\n无论是实施新的 QMS、整改检查发现项、外包质量活动,还是确保合格人员到位,选择合适的合作伙伴都至关重要。",
      whyQbd: {
        intro:
          "QbD Group 为各种规模的组织带来数十年的质量保证经验。我们由 150 多名 QA 专家组成的团队从战略到执行,在每个阶段为您提供支持。",
        bullets: [
          "150 多名 QA 专家,在中小型与大型组织中均有丰富经验。",
          "深厚的法规知识,与主管当局有大量互动经验。",
          "针对您组织独特挑战的定制化解决方案。",
          "全球支持能力,可根据您的需要提供现场或远程服务。",
        ],
      },
      formHeading: "聊聊质量保证",
      formSubtitle:
        "无论您需要实施 QMS、外包 QA,还是填补 QP/RP 角色,我们的专家都已准备好为您提供帮助。",
    },
  },
  "toxicology": {
    es: {
      title: "Toxicología Regulatoria",
      subtitle: "Evaluaciones toxicológicas expertas para la industria farmacéutica y química",
      description:
        "Desde informes PDE/ADE hasta evaluaciones de riesgo de nitrosaminas y registro REACH, nuestro equipo de toxicólogos certificados EUROTOX ofrece informes científicamente sólidos y conformes con la normativa.",
      valueProps: [
        {
          title: "Toxicólogos certificados EUROTOX (ERT)",
          description:
            "Todos nuestros informes son preparados y revisados por toxicólogos certificados, asegurando los más altos estándares de calidad científica y fiabilidad regulatoria.",
        },
        {
          title: "Más de 3.500 compuestos evaluados",
          description:
            "Amplia experiencia en la evaluación de principios activos farmacéuticos para más de 600 clientes en más de 50 países.",
        },
        {
          title: "Enfoque integral de la toxicología",
          description:
            "Desde el desarrollo temprano hasta los cambios post-aprobación, proporcionamos evaluaciones de riesgo integradas alineadas con estrategias de QA, CMC y regulatorias.",
        },
        {
          title: "Presencia en más de 60 países",
          description:
            "Nuestro alcance global garantiza experiencia toxicológica dondequiera que se fabriquen o comercialicen tus productos.",
        },
      ],
      challengesTitle: "Retos en toxicología farmacéutica",
      challengesContent:
        "La regulación de impurezas evoluciona constantemente, con nuevas directrices de la EMA y la FDA que exigen evaluaciones más detalladas y controles más estrictos.\n\nDesde las nitrosaminas hasta las impurezas elementales y los disolventes residuales, las empresas farmacéuticas necesitan socios de confianza que combinen experiencia científica con conocimiento regulatorio profundo para garantizar el cumplimiento y la seguridad del paciente.",
      whyQbd: {
        intro:
          "QbD Group aporta décadas de experiencia en toxicología farmacéutica, con un equipo de toxicólogos certificados EUROTOX que ofrecen evaluaciones científicamente rigurosas y regulatoriamente conformes.",
        bullets: [
          "Toxicólogos certificados EUROTOX (ERT) en cada proyecto.",
          "Más de 3.500 compuestos evaluados para más de 600 clientes globales.",
          "Supervisión proactiva del cumplimiento para anticiparse a las regulaciones en evolución.",
          "Soporte integral desde el desarrollo temprano hasta los cambios post-aprobación.",
        ],
      },
      formHeading: "Hablemos de Toxicología Regulatoria",
      formSubtitle:
        "Ya necesites informes PDE, evaluaciones de riesgo de nitrosaminas o soporte REACH, nuestros toxicólogos certificados están listos para ayudarte.",
    },
    "zh-cn": {
      title: "法规毒理学",
      subtitle: "面向制药与化学行业的专业毒理学评估",
      description:
        "从 PDE/ADE 报告到亚硝胺风险评估和 REACH 注册,我们的 EUROTOX 认证毒理学家团队提供科学严谨且符合法规的报告。",
      valueProps: [
        {
          title: "EUROTOX 认证毒理学家(ERT)",
          description:
            "我们所有的报告均由认证毒理学家撰写与审核,确保最高水准的科学质量与法规可信度。",
        },
        {
          title: "已评估超过 3,500 种化合物",
          description:
            "在为分布于 50 多个国家的 700 多家客户评估药用活性成分方面拥有丰富经验。",
        },
        {
          title: "全方位的毒理学方法",
          description:
            "从早期开发到上市后变更,我们提供与 QA、CMC 和法规策略相一致的整合式风险评估。",
        },
      ],
      challengesTitle: "制药毒理学的挑战",
      challengesContent:
        "杂质监管不断演变,EMA 与 FDA 的新指南要求更详细的评估和更严格的控制。\n\n从亚硝胺到元素杂质和残留溶剂,制药公司需要兼具科学专长与深入法规知识的可信赖合作伙伴,以确保合规与患者安全。",
      whyQbd: {
        intro:
          "QbD Group 在制药毒理学领域拥有数十年的经验,我们的 EUROTOX 认证毒理学家团队提供科学严谨且符合法规的评估。",
        bullets: [
          "每个项目均配备 EUROTOX 认证毒理学家(ERT)。",
          "已为 700 多家全球客户评估超过 3,500 种化合物。",
          "主动的合规监控,以提前应对不断演变的法规。",
          "从早期开发到上市后变更的全方位支持。",
        ],
      },
      formHeading: "聊聊法规毒理学",
      formSubtitle:
        "无论您需要 PDE 报告、亚硝胺风险评估还是 REACH 支持,我们的认证毒理学家都已准备好为您提供帮助。",
    },
  },
  "pharmacovigilance": {
    es: {
      title: "Pharmacovigilance",
      subtitle: "Seguridad pre-comercialización y post-comercialización",
      description:
        "En QbD Group entendemos que la seguridad es primordial en un mundo de cumplimiento estricto. Desde la evaluación de riesgos pre-comercialización y la planificación de seguridad clínica hasta la vigilancia post-comercialización y la notificación de eventos adversos, garantizamos la seguridad y el cumplimiento en todo el ciclo de vida del producto para medicamentos, Medical Devices, suplementos alimenticios y cosméticos.",
      valueProps: [
        {
          title: "Servicios de principio a fin",
          description:
            "Desde la notificación de eventos adversos hasta la mitigación de riesgos, proporcionamos soluciones integrales de farmacovigilancia que cubren todos los aspectos de tus obligaciones de seguridad.",
        },
        {
          title: "Eficiencia como motor",
          description:
            "Soluciones rentables y optimizadas diseñadas para escalar con las necesidades crecientes, aprovechando la tecnología y proporcionando monitorización y notificación exhaustivas.",
        },
        {
          title: "Experiencia global y local",
          description:
            "Soluciones estratégicas 'Top-Down' que alinean los estándares globales de farmacovigilancia con las necesidades del mercado local, garantizando el cumplimiento y la ventaja competitiva.",
        },
        {
          title: "Tecnológicamente agnósticos",
          description:
            "Flexibilidad en la integración de una amplia gama de tecnologías para mantenernos continuamente adaptados y receptivos a las necesidades cambiantes de nuestros clientes.",
        },
      ],
      challengesTitle: "Eficiencia y cumplimiento: de lo global a lo local",
      challengesContent:
        "En QbD Group, ofrecemos soluciones de farmacovigilancia (FV) de principio a fin que escalan con tu negocio — apoyando tanto a las sedes centrales como a las filiales locales con el mismo nivel de calidad, flexibilidad y cumplimiento.\n\n**Para las sedes centrales: plena responsabilidad, visión global**\nResponsabilidad total sobre tus obligaciones de farmacovigilancia y seguridad. Soporte estratégico para navegar con confianza las complejas regulaciones globales. Gobernanza sólida de proyectos mediante gestión global, minimizando la necesidad de supervisión diaria por parte del cliente. Soporte escalable con acceso a recursos cualificados en todas las áreas funcionales. Soluciones tecnológicamente agnósticas adaptadas a tus necesidades.\n\n**Para las filiales locales: excelencia en FV, con raíces locales**",
      whyQbd: {
        intro: "¿Por qué QbD Group para farmacovigilancia?",
        bullets: [
          "Modelos de externalización escalables y flexibles — ofrecemos opciones funcionales, híbridas y de externalización completa adaptadas a la fase de tu negocio, carga de trabajo y prioridades estratégicas.",
          "Cobertura global con experiencia local — nuestros equipos están integrados en toda Europa con profundo conocimiento del panorama regulatorio local.",
          "Soluciones impulsadas por la tecnología — aprovechamos herramientas y plataformas de última generación para optimizar la eficiencia y el cumplimiento.",
          "Trayectoria demostrada — décadas de experiencia apoyando a empresas farmacéuticas, biotecnológicas y de Medical Devices con farmacovigilancia de primer nivel.",
        ],
      },
      formHeading: "Hablemos de farmacovigilancia",
      formSubtitle:
        "Desde los servicios de QPPV hasta la gestión de señales, nuestros expertos en seguridad están listos para apoyar tus necesidades de farmacovigilancia.",
    },
    "zh-cn": {
      title: "药物警戒",
      subtitle: "上市前与上市后的安全性",
      description:
        "在 QbD Group,我们深知在严格合规的世界中,安全性至关重要。从上市前风险评估和临床安全规划到上市后监测和不良事件报告,我们为药品、医疗器械、膳食补充剂和化妆品在整个产品生命周期内的安全与合规提供保障。",
      valueProps: [
        {
          title: "端到端服务",
          description:
            "从不良事件报告到风险缓解,我们提供覆盖您所有安全义务的全方位药物警戒解决方案。",
        },
        {
          title: "以效率为驱动",
          description:
            "经济高效、流程优化的解决方案,可随业务需求扩展,借助技术提供全面的监测与报告。",
        },
        {
          title: "全球与本地经验兼备",
          description:
            "采用'自上而下'的战略性方案,将全球药物警戒标准与本地市场需求对齐,确保合规与竞争优势。",
        },
        {
          title: "技术中立",
          description:
            "在整合多种技术方面保持灵活性,持续适应并响应客户不断变化的需求。",
        },
      ],
      challengesTitle: "效率与合规:从全球到本地",
      challengesContent:
        "在 QbD Group,我们提供端到端的药物警戒(PV)解决方案,可与您的业务一同扩展 — 以同等的质量、灵活性与合规性为总部及本地子公司提供支持。\n\n**面向总部:全责承担,全球视野**\n全面承担您的药物警戒与安全义务。提供战略性支持,自信应对复杂的全球法规。通过全球化管理实现稳健的项目治理,最大限度减少客户日常监督的需要。具备访问各职能领域合格资源的可扩展支持。技术中立的解决方案以适配您的需求。\n\n**面向本地子公司:以本地化扎根,提供卓越的 PV**",
      whyQbd: {
        intro: "为何选择 QbD Group 提供药物警戒服务?",
        bullets: [
          "可扩展且灵活的外包模式 — 提供职能型、混合型与完全外包等选项,以匹配您的业务阶段、工作量与战略优先级。",
          "全球覆盖与本地经验 — 我们的团队遍布欧洲,深入了解当地法规环境。",
          "技术驱动的解决方案 — 借助先进的工具与平台,优化效率与合规性。",
          "成熟业绩 — 数十年来支持制药、生物技术和医疗器械企业,提供顶级药物警戒服务。",
        ],
      },
      formHeading: "聊聊药物警戒",
      formSubtitle:
        "从 QPPV 服务到信号管理,我们的安全性专家随时为您的药物警戒需求提供支持。",
    },
  },
  "lab-services": {
    es: {
      title: "Lab Services",
      subtitle: "Calidad por control",
      description:
        "Con más de 25 años de experiencia, los servicios de laboratorio de QbD Group ofrecen soluciones analíticas de alta calidad adaptadas a la industria farmacéutica. Nuestro laboratorio conforme con GMP combina tiempos de respuesta eficientes, precios competitivos y un enfoque flexible y personalizado.",
      valueProps: [
        {
          title: "Tiempos de respuesta eficientes",
          description:
            "Nuestros flujos de trabajo optimizados y nuestro equipo experto ofrecen resultados rápidos y precisos sin comprometer la calidad.",
        },
        {
          title: "Precios competitivos",
          description:
            "Al optimizar procesos e implementar flujos de trabajo lean, aseguramos servicios de alta calidad a precios que se adaptan a tu presupuesto.",
        },
        {
          title: "Experiencia y enfoque personalizado",
          description:
            "Con más de 25 años de experiencia, apoyamos tanto a multinacionales farmacéuticas como a start-ups, adaptando las soluciones a tus necesidades únicas.",
        },
        {
          title: "Calidad sin concesiones",
          description:
            "Nuestro equipo altamente cualificado realiza los análisis con equipos de última generación, operando en condiciones GMP para asegurar el cumplimiento y la precisión.",
        },
      ],
      challengesTitle: "Tendencias del mercado y desafíos",
      challengesContent:
        "La **demanda de precisión, rapidez y cumplimiento** en los servicios de laboratorio nunca ha sido mayor. A medida que las industrias se enfrentan a un escrutinio regulatorio cada vez más exigente, la necesidad de ensayos precisos y procesos optimizados es primordial. Desde la detección de impurezas hasta la garantía de estabilidad de los lotes, los ensayos de laboratorio exigen ahora tecnología avanzada y experiencia cualificada.\n\nLa **globalización** añade complejidad, con empresas que navegan regulaciones variadas en diferentes regiones. El impulso hacia la sostenibilidad también crea nuevos retos, ya que las industrias buscan formas innovadoras de validar materiales y procesos respetuosos con el medio ambiente.\n\nLos servicios de laboratorio de QbD Group ofrecen la experiencia y las soluciones a medida necesarias para satisfacer estas demandas. Ya sea validación de métodos, estudios de estabilidad o liberación de lotes, te ayudamos a mantenerte a la vanguardia ofreciendo resultados fiables y accionables.",
      whyQbd: {
        intro:
          "Los servicios de laboratorio de QbD Group combinan más de 25 años de experiencia analítica con un enfoque centrado en el cliente. Nuestro laboratorio conforme con GMP ofrece precisión, fiabilidad y rapidez para todas tus necesidades de ensayos farmacéuticos.",
        bullets: [
          "+25 años de experiencia analítica — profundo conocimiento en métodos de ensayo farmacéutico y estándares regulatorios.",
          "Soluciones a medida — adaptamos nuestros servicios a tus necesidades específicas, ya seas una start-up o una multinacional.",
          "Alcance global — nuestro laboratorio atiende a clientes en toda Europa con calidad y cumplimiento constantes.",
          "Compromiso con la calidad — equipos de última generación y condiciones GMP para resultados fiables y precisos en cada ocasión.",
        ],
      },
      formHeading: "Hablemos de servicios de laboratorio",
      formSubtitle:
        "Desde la validación de métodos hasta los estudios de estabilidad, nuestros expertos de laboratorio están listos para apoyar tus necesidades analíticas.",
    },
    "zh-cn": {
      title: "制药实验室服务",
      subtitle: "通过控制实现质量",
      description:
        "凭借 25 年以上的经验,QbD Group 实验室服务为制药行业提供量身定制的高质量分析解决方案。我们符合 GMP 的实验室融合了高效的周转时间、具有竞争力的价格,以及灵活、个性化的方法。",
      valueProps: [
        {
          title: "高效的周转时间",
          description:
            "我们优化的工作流程与专家团队在不影响质量的前提下,提供快速、精准的结果。",
        },
        {
          title: "具有竞争力的价格",
          description:
            "通过流程优化与精益工作流的实施,我们以适合您预算的价格提供高质量服务。",
        },
        {
          title: "经验与个性化方法",
          description:
            "凭借 25 年以上的经验,我们既支持大型跨国药企,也支持初创企业,根据您的独特需求定制解决方案。",
        },
        {
          title: "毫不妥协的质量",
          description:
            "我们高素质的团队使用先进设备开展分析,在 GMP 条件下运营,确保合规与精准。",
        },
      ],
      challengesTitle: "市场趋势与挑战",
      challengesContent:
        "对实验室服务在**精确性、速度与合规性**方面的需求前所未有。随着各行业面临日益严苛的法规审查,精准检测与优化流程的需求至关重要。从杂质检测到批次稳定性保障,实验室检测如今要求先进的技术与专业经验。\n\n**全球化**进一步增加了复杂性,企业需要在不同地区应对各异的法规。可持续性的推动也带来新挑战,各行业正在寻找创新方法来验证环境友好的材料与流程。\n\nQbD Group 实验室服务提供满足这些需求所需的专业能力与定制化解决方案。无论是方法验证、稳定性研究还是批次放行,我们都帮助您以可靠、可执行的结果保持领先。",
      whyQbd: {
        intro:
          "QbD Group 实验室服务将 25 年以上的分析经验与以客户为中心的方法相结合。我们符合 GMP 的实验室为您所有的制药检测需求提供精准、可靠和迅速的服务。",
        bullets: [
          "25 年以上的分析经验 — 在制药检测方法和法规标准方面拥有深厚专长。",
          "量身定制的解决方案 — 我们根据您的具体需求调整服务,无论您是初创企业还是跨国公司。",
          "全球布局 — 我们的实验室以一致的质量与合规性服务整个欧洲的客户。",
          "对质量的承诺 — 先进的设备与 GMP 条件,每一次都提供可靠、精确的结果。",
        ],
      },
      formHeading: "聊聊实验室服务",
      formSubtitle:
        "从方法验证到稳定性研究,我们的实验室专家随时为您的分析需求提供支持。",
    },
  },
  "software-solutions-services": {
    es: {
      title: "Software Solutions & Services",
      subtitle: "Donde el cumplimiento, la eficiencia y la innovación impulsan el éxito",
      description:
        "No te quedes atrás en la transformación digital de la industria de Life Sciences. QbD Group aprovecha su profunda experiencia en procesos de negocio, requisitos regulatorios y TI. Proporcionamos soluciones de software a medida y servicios integrales para ayudarte a encontrar el equilibrio adecuado entre cumplimiento y eficiencia empresarial.",
      valueProps: [
        {
          title: "Experiencia",
          description:
            "Nuestro equipo combina un profundo conocimiento de Life Sciences con experiencia en TI para ofrecer soluciones que cumplen tanto los requisitos regulatorios como los de negocio.",
        },
        {
          title: "Enfoque tecnológicamente agnóstico",
          description:
            "Nuestro enfoque garantiza que tus necesidades únicas determinen las soluciones que proporcionamos, no las limitaciones de una tecnología específica.",
        },
        {
          title: "Cobertura completa de Life Sciences",
          description:
            "Nuestras soluciones y servicios cubren todas las áreas de Life Sciences, desde Medical Devices (MD) y In Vitro Diagnostics (IVD) hasta ATMP y medicamentos.",
        },
        {
          title: "Soporte durante todo el ciclo de vida",
          description:
            "Te ayudamos a navegar las complejidades de la transformación digital con confianza y agilidad, asegurando que todo tu recorrido sea lo más rápido y exitoso posible.",
        },
      ],
      challengesTitle: "Tendencias del mercado y desafíos",
      challengesContent:
        "En el panorama digital en rápida evolución, las organizaciones de Life Sciences se enfrentan a **requisitos regulatorios crecientes** y una presión cada vez mayor para **optimizar la eficiencia**. La validación de software, la integridad de datos y la digitalización de sistemas son ahora críticas para garantizar el cumplimiento mientras se impulsan mejoras operativas.\n\nAl mismo tiempo, las empresas deben adaptarse a **nuevas tecnologías** y abordar **riesgos de ciberseguridad** sin comprometer la fiabilidad del sistema. El cambio hacia plataformas basadas en la nube y la automatización crea tanto oportunidades como retos, requiriendo experiencia y una estrategia orientada al futuro.\n\nQbD Group te ayuda a navegar estas complejidades con soluciones de software a medida que priorizan el cumplimiento, optimizan los procesos y preparan a tu organización para las demandas del mañana.",
      whyQbd: {
        intro:
          "QbD Group combina un profundo conocimiento de Life Sciences con experiencia en TI. Nuestro equipo de software te ayuda a navegar la transformación digital manteniendo el pleno cumplimiento regulatorio.",
        bullets: [
          "Experiencia demostrada — décadas de experiencia en validación de software, integridad de datos e implementación de sistemas en Life Sciences.",
          "Soporte a medida — adaptamos nuestros servicios a tus necesidades específicas, tu stack tecnológico y tus requisitos regulatorios.",
          "Presencia global — nuestros equipos atienden a clientes en toda Europa con calidad y experiencia constantes.",
          "Servicios de ciclo de vida completo — desde la selección del sistema hasta el soporte continuo, cubrimos cada fase de tu recorrido digital.",
        ],
      },
      formHeading: "Hablemos de software",
      formSubtitle:
        "Desde la validación de sistemas hasta la transformación digital, nuestros expertos en software están listos para acompañarte.",
    },
    "zh-cn": {
      title: "面向制药与 MedTech 的软件解决方案",
      subtitle: "在合规、效率与创新交汇处驱动成功",
      description:
        "不要在 Life Sciences 行业的数字化转型中落后。QbD Group 凭借在业务流程、法规要求和 IT 方面的深厚专长,为您提供量身定制的软件解决方案与全方位服务,帮助您在合规与业务效率之间找到合适的平衡。",
      valueProps: [
        {
          title: "专业经验",
          description:
            "我们的团队将深厚的 Life Sciences 知识与 IT 专长相结合,提供同时满足法规与业务需求的解决方案。",
        },
        {
          title: "技术中立的方法",
          description:
            "我们的方法确保由您的独特需求决定我们提供的解决方案,而非受制于特定技术。",
        },
        {
          title: "覆盖完整的 Life Sciences 领域",
          description:
            "我们的解决方案与服务涵盖 Life Sciences 的所有领域,从医疗器械(MD)、体外诊断(IVD)到 ATMP 和药品。",
        },
        {
          title: "全生命周期支持",
          description:
            "我们帮助您自信、敏捷地应对数字化转型的复杂性,确保您的整个旅程尽可能快速且成功。",
        },
      ],
      challengesTitle: "市场趋势与挑战",
      challengesContent:
        "在快速演变的数字化环境中,Life Sciences 组织面临**不断增加的法规要求**以及日益增长的**优化效率**的压力。软件验证、数据完整性与系统数字化已成为在确保合规的同时推动运营改进的关键。\n\n与此同时,企业必须适应**新技术**并应对**网络安全风险**,同时不影响系统的可靠性。向云平台与自动化的转变既带来机遇也带来挑战,需要经验和面向未来的战略。\n\nQbD Group 通过量身定制的软件解决方案,帮助您应对这些复杂性,优先考虑合规、优化流程,并使您的组织为未来的需求做好准备。",
      whyQbd: {
        intro:
          "QbD Group 将深厚的 Life Sciences 知识与 IT 专长相结合。我们的软件团队帮助您在保持完全法规合规的同时,推动数字化转型。",
        bullets: [
          "成熟经验 — 在 Life Sciences 领域的软件验证、数据完整性和系统实施方面拥有数十年经验。",
          "量身支持 — 我们根据您的具体需求、技术栈和法规要求调整服务。",
          "全球布局 — 我们的团队以一致的质量与专业性为整个欧洲的客户提供服务。",
          "全生命周期服务 — 从系统选型到持续支持,覆盖您数字化旅程的每个阶段。",
        ],
      },
      formHeading: "聊聊软件",
      formSubtitle:
        "从系统验证到数字化转型,我们的软件专家随时为您提供支持。",
    },
  },
  "regulatory-affairs": {
    es: {
      title: "Regulatory Affairs",
      subtitle: "Tu experto regulatorio en Europa",
      description:
        "Lleva tu producto al mercado con confianza gracias al soporte experto en Regulatory Affairs. Desde la estrategia hasta la aprobación del producto, ofrecemos servicios personalizados para navegar requisitos complejos y cumplir con los estándares europeos. Ya sea compilar expedientes técnicos, gestionar dossiers o coordinar con organismos reguladores, nos encargamos de los detalles para que tú puedas centrarte en la innovación.",
      valueProps: [
        {
          title: "Experiencia en Pharma & Biotech",
          description:
            "Profundo conocimiento regulatorio para productos farmacéuticos y biotecnológicos en el mercado europeo.",
        },
        {
          title: "Dominio en Medical Devices",
          description:
            "Orientación experta en requisitos MDR, documentación técnica e interacciones con organismos notificados.",
        },
        {
          title: "Soporte regulatorio para IVD",
          description:
            "Soporte integral para el cumplimiento del IVDR, desde la clasificación hasta la evaluación del rendimiento y el lanzamiento al mercado.",
        },
        {
          title: "Orientación en Combination Products",
          description:
            "Navega el complejo panorama regulatorio de los Combination Products con nuestra experiencia multifuncional.",
        },
      ],
      challengesTitle: "",
      challengesContent: "",
      whyQbd: {
        intro:
          "El equipo de Regulatory Affairs de QbD Group combina una profunda experiencia en la industria con un enfoque pragmático y práctico. Ayudamos a las empresas de Life Sciences a navegar eficientemente los complejos panoramas regulatorios europeos, desde la estrategia inicial hasta el cumplimiento post-comercialización.",
        bullets: [
          "Experiencia multisectorial: nuestros especialistas regulatorios cubren Pharma, Biotech, Medical Devices, IVD y Combination Products bajo un mismo techo.",
          "Planificación regulatoria estratégica: alineamos tu ruta regulatoria con tus objetivos comerciales para acelerar el time-to-market.",
          "Trayectoria demostrada: con miles de presentaciones exitosas y proyectos de gestión de dossiers en toda Europa, ofrecemos resultados de confianza.",
          "Soporte escalable: desde una consulta regulatoria puntual hasta operaciones regulatorias completamente externalizadas, nos adaptamos a tus necesidades.",
        ],
      },
      formHeading: "Hablemos de Regulatory Affairs",
      formSubtitle:
        "Desde la estrategia regulatoria hasta la aprobación del producto, nuestros expertos están listos para guiarte en cada paso.",
      industryLinks: [
        {
          title: "Pharma & Biotech",
          description:
            "Navega regulaciones globales complejas con confianza gracias a la orientación experta a lo largo de todo el ciclo de vida del producto. Te ayudamos a acelerar aprobaciones y mantener el cumplimiento a largo plazo.",
        },
        {
          title: "Medical Devices",
          description:
            "Lleva al mercado Medical Devices seguros y conformes de forma más rápida con soporte regulatorio estratégico adaptado a tu tecnología. Desde la clasificación hasta la presentación, guiamos cada paso.",
        },
        {
          title: "In Vitro Diagnostics",
          description:
            "Asegura que tus productos de In Vitro Diagnostics cumplan con los estándares regulatorios en constante evolución en todo el mundo. Nuestros expertos te apoyan en la construcción de expedientes robustos y conformes.",
        },
        {
          title: "Combination Products",
          description:
            "Gestiona los retos regulatorios únicos de la integración fármaco-dispositivo con claridad y precisión. Optimizamos tu ruta hacia la aprobación con la estrategia correcta desde el inicio.",
        },
      ],
    },
    "zh-cn": {
      title: "法规事务",
      subtitle: "您在欧洲的法规专家",
      description:
        "凭借专业的法规事务支持,自信地将您的产品推向市场。从战略到产品获批,我们提供量身定制的服务,帮助您应对复杂要求并满足欧洲标准。无论是编制技术档案、管理 dossier,还是与法规机构协调,我们都会处理好细节,让您专注于创新。",
      valueProps: [
        {
          title: "制药与生物技术专长",
          description:
            "在欧洲市场,为医药与生物技术产品提供深厚的法规知识。",
        },
        {
          title: "医疗器械专长",
          description:
            "在 MDR 要求、技术文档以及与公告机构互动方面提供专家指导。",
        },
        {
          title: "面向 IVD 的法规支持",
          description:
            "为 IVDR 合规提供全方位支持,从分类到性能评估再到产品上市。",
        },
        {
          title: "组合产品指导",
          description:
            "依托我们的多职能专长,应对组合产品复杂的法规环境。",
        },
      ],
      challengesTitle: "",
      challengesContent: "",
      whyQbd: {
        intro:
          "QbD Group 的法规事务团队将深厚的行业经验与务实的实践方法相结合。我们帮助 Life Sciences 企业高效应对复杂的欧洲法规环境,从初期战略到上市后合规。",
        bullets: [
          "跨领域专长:我们的法规专家涵盖制药、生物技术、医疗器械、IVD 和组合产品,皆在同一屋檐下。",
          "战略性法规规划:将您的法规路径与商业目标对齐,加快上市时间。",
          "成熟业绩:在欧洲范围内完成数千次成功提交与档案管理项目,提供值得信赖的成果。",
          "可扩展支持:从一次性法规咨询到完全外包的法规运营,我们因您所需而灵活调整。",
        ],
      },
      formHeading: "聊聊法规事务",
      formSubtitle:
        "从法规战略到产品获批,我们的专家已准备好在每一步为您提供指导。",
      industryLinks: [
        {
          title: "制药与生物技术",
          description:
            "在产品全生命周期内借助专家指导,自信应对复杂的全球法规。我们帮助您加快审批并保持长期合规。",
        },
        {
          title: "医疗器械",
          description:
            "通过为您的技术量身定制的战略性法规支持,更快地将安全、合规的医疗器械推向市场。从分类到提交,我们指导每一步。",
        },
        {
          title: "体外诊断",
          description:
            "确保您的体外诊断产品在全球范围内符合不断演变的法规标准。我们的专家协助您构建稳健、合规的技术档案。",
        },
        {
          title: "组合产品",
          description:
            "以清晰与精准应对药械整合所带来的独特法规挑战。我们以恰当的初始策略,优化您通向获批的路径。",
        },
      ],
    },
  },
};

/**
 * Get translated service content for a given slug and language.
 * Returns undefined if no translation exists (fallback to English data).
 */
export const getServiceContent = (slug: string, lang: SiteLang): ServiceContentI18n | undefined => {
  if (lang === "en") return undefined; // English uses the default servicesData
  return serviceContent[slug]?.[lang];
};

/** Returns the set of service slugs that have a translation for the given language */
export const getTranslatedServiceSlugs = (lang: SiteLang): Set<string> => {
  if (lang === "en") return new Set();
  const slugs = new Set<string>();
  for (const [slug, translations] of Object.entries(serviceContent)) {
    if (translations[lang]) slugs.add(slug);
  }
  return slugs;
};
